<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { BizCardRouteForm } from '@client/pages/bizcards/routing/modals/shared';

  import { BizCardsService } from '@client/services/bizcards';
  import { BizCardsRoutingService } from '@client/services/bizcards-routing';
  import { GroupsService } from '@client/services/groups';

  import { appStore } from '@client/stores/app';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let route = { priority: 0, selectedGroups: [] };
  let senderGroups = [];
  let groupOptions = [];
  let businessCardOptions = [];

  const onSignatureChange = async (e) => {
    const blockname = e.detail.value;
    const response = await BizCardsService.getById(blockname);

    route = {
      ...route,
      bizcard: response,
      blockname
    };
  };

  const executeCreate = async () => {
    $appStore.loading = true;

    const { blockname, bizcard = {} } = route;

    try {
      const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
      const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));

      const payload = {
        blockname,
        bizcard,
        priority: 0,
        sendergroups,
        status: route.status,
        tags: []
      };

      const response = await BizCardsRoutingService.updateRoute(payload);

      // Get all routes and reorder to place new route at the top
      const allRoutes = await BizCardsRoutingService.getAll();
      const ids = allRoutes.map(({ _id }) => _id);

      // Move the new route to the front
      const newRouteIndex = ids.indexOf(response._id);
      if (newRouteIndex > 0) {
        ids.splice(newRouteIndex, 1);
        ids.unshift(response._id);
        await BizCardsRoutingService.saveRouteOrder(ids);
      }

      notify($format('label.SUCCESS.CREATE_ROUTE_FOR', { values: { blockname } }));

      step.data = {
        id: response.blockname,
        rollbackData: {
          routeId: response._id
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.CREATE_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackCreate = async () => {
    $appStore.loading = true;

    try {
      const { routeId } = step.data.rollbackData;

      if (!routeId) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      // Get the route to delete
      const allRoutes = await BizCardsRoutingService.getAll();
      const route = allRoutes.find((r) => r._id === routeId);

      if (!route) {
        throw new Error($format('label.FAILURE.ROUTE_NOT_FOUND'));
      }

      await BizCardsRoutingService.deleteRoute(route);

      notify($format('label.SUCCESS.ROLLBACK_ROUTE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      const [groupsResponse, bizcardsResponse] = await Promise.all([GroupsService.getAll(), BizCardsService.getAll()]);

      senderGroups = Array.isArray(groupsResponse) ? groupsResponse : [];
      groupOptions = senderGroups.map((group) => ({
        label: group.groupname,
        value: group.groupname
      }));

      const cards = Array.isArray(bizcardsResponse) ? bizcardsResponse : [];
      businessCardOptions = cards
        .filter(({ archived }) => !archived)
        .map(({ name, rulename }) => ({
          label: name || rulename,
          value: rulename
        }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    } catch (error) {
      console.error('Failed to load bizcard routing data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeCreate);
    }

    if (onRollback) {
      onRollback(rollbackCreate);
    }
  });

  $: nextDisabled = !route.blockname || !route.selectedGroups?.length || loading;
</script>

<div class="bizcard-routing-create-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <BizCardRouteForm bind:route bind:businessCardOptions bind:groupOptions {onSignatureChange} />
  {/if}
</div>

<style lang="scss">
  .bizcard-routing-create-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }
  }
</style>
